import { useQuery } from '@tanstack/react-query';
import { Alert, Badge, Fact, NotProvided, SideDrawer } from '@evogard/ui';
import type { CatalogVehicleDetail, ClaimSummary } from '@evogard/contract';
import {
  formatDocument,
  formatInstant,
  formatPhone,
  loadFailureMessage,
} from '@evogard/domain';
import { StatusBadge } from '../../../components/StatusBadge.js';
import { api } from '../../../lib/api.js';
import { useThirdParty } from '../third-parties/useThirdParties.js';
import { Section, Wait } from './RepairBlock.js';

/**
 * Quem é a pessoa de um aviso — o associado ou o terceiro —, na gaveta da
 * mesa (08/10/2026): a mesma do reparo e do ocorrido, aberta pelo nome dela
 * nos envolvidos. A ficha do aviso abre pelo botão do alto da mesa.
 *
 * O desenho é o do protótipo da mesa dos departamentos: o selo da situação,
 * e seções curtas de rótulo e valor. Só o que o sistema tem: a mensalidade e
 * a CNH do protótipo não existem em tabela nenhuma, e a gaveta não as inventa
 * (regra 14). O plano e os benefícios entraram em 29/10/2026, da ficha.
 *
 * Só leitura, e nada é novo: o associado vem da ficha do veículo no núcleo
 * (`erp.vehicle`, a mesma de ERP › Veículos, pelo `VE-000123`), e o terceiro,
 * do cadastro de terceiros (`getThirdParty`). O que a linha do aviso já traz
 * — o nome, a placa e o modelo — aparece mesmo quando a consulta é recusada.
 */
export function PersonDrawer({
  person,
  onClose,
}: {
  person: ClaimSummary;
  onClose: () => void;
}) {
  const third = person.type === 'terceiro';
  const name = third ? person.thirdParty?.name : person.vehicle?.memberName;
  return (
    <SideDrawer
      eyebrow={
        third
          ? `Terceiro do aviso #${person.parentClaimId} · aviso #${person.id}`
          : `Associado · aviso #${person.id}`
      }
      title={name ?? 'Nome não informado'}
      closeLabel={third ? 'Fechar os dados do terceiro' : 'Fechar os dados do associado'}
      onClose={onClose}
    >
      {third ? <ThirdPartyData claim={person} /> : <MemberData claim={person} />}
    </SideDrawer>
  );
}

function MemberData({ claim }: { claim: ClaimSummary }) {
  const code = claim.vehicle?.code ?? null;
  /* A mesma chave da ficha de ERP › Veículos: abrir uma aproveita a outra. */
  const query = useQuery({
    queryKey: ['erp', 'veiculo', code],
    queryFn: async () => {
      const r = await api.erp.vehicle({ params: { code: code! } });
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'o associado'));
      return r.body;
    },
    enabled: code !== null,
  });
  const v = query.data;
  const owner = v?.owner;

  if (code === null) {
    return (
      <Wait>
        O veículo deste aviso (código {claim.vehicleCode} no SGA) ainda não está no
        cadastro da casa, e é por ele que se chega ao associado.
      </Wait>
    );
  }

  return (
    <>
      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
      {query.isLoading && (
        <p className="text-sm text-texto-suave">Carregando o associado…</p>
      )}

      {owner?.situacao && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-texto-suave">
          {/* o selo da casa: a cor sai da classificação, e não de um `/ativ/i` sobre o texto */}
          <StatusBadge situacao={owner.situacao} />
          <span>Código no ERP {owner.code}</span>
        </div>
      )}

      {v && !owner && <Wait>Este veículo não aponta associado no cadastro do ERP.</Wait>}

      {owner && (
        <Section title="Contato">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Fact label="Documento">
              {owner.documento ? (
                <span className="inline-flex items-center gap-2 tabular-nums">
                  {owner.documento}
                  {!owner.documentRevealed && <Badge tone="neutro">mascarado</Badge>}
                </span>
              ) : (
                <NotProvided />
              )}
            </Fact>
            <Fact label="Telefone">
              {owner.phones.length > 0 ? (
                <span className="tabular-nums">{owner.phones.join(' · ')}</span>
              ) : (
                <NotProvided />
              )}
            </Fact>
            <Fact label="E-mail" wide>
              {owner.email ?? <NotProvided />}
            </Fact>
            <Fact label="Endereço" wide>
              {owner.address ?? <NotProvided />}
            </Fact>
          </dl>
        </Section>
      )}

      <Section title="Veículo protegido">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
          <Fact label="Placa">
            {(v?.placa ?? claim.vehicle?.plate) ? (
              <span className="font-mono tabular-nums">
                {v?.placa ?? claim.vehicle?.plate}
              </span>
            ) : (
              <NotProvided />
            )}
          </Fact>
          <Fact label="Ano">
            {(v?.anoModelo ?? claim.vehicle?.modelYear) || <NotProvided />}
          </Fact>
          <Fact label="Modelo" wide>
            {[v?.marca ?? claim.vehicle?.brand, v?.modelo ?? claim.vehicle?.model]
              .filter(Boolean)
              .join(' ') || <NotProvided />}
          </Fact>
          {v && (
            <Fact label="Chassi">
              {v.chassi ? (
                <span className="font-mono tabular-nums">{v.chassi}</span>
              ) : (
                <NotProvided />
              )}
            </Fact>
          )}
          <Fact label="Código">{code}</Fact>
        </dl>
      </Section>

      {v && <PlanData vehicle={v} />}
    </>
  );
}

/**
 * O plano e os benefícios do carro (29/10/2026, pedido do dono): o plano é o
 * grupo de produto do ERP, e os benefícios são os produtos do contrato —
 * "carro reserva 30 dias", "auxílio gard 15 dias" —, os mesmos da ficha de
 * ERP › Veículos. Os ativos primeiro; o inativo aparece marcado, porque o ERP
 * ainda o liga ao carro.
 *
 * "Nunca consultado" não é "sem benefício": a esteira pergunta os produtos aos
 * poucos, e a gaveta diz isso em vez de mostrar uma lista vazia.
 */
function PlanData({ vehicle: v }: { vehicle: CatalogVehicleDetail }) {
  const products = [...v.products].sort(
    (a, b) => Number(b.erpStatus === 'ATIVO') - Number(a.erpStatus === 'ATIVO'),
  );
  return (
    <Section title="Plano e benefícios">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
        <Fact label="Plano" wide>
          {v.plan === null ? (
            <NotProvided>o ERP não aponta plano</NotProvided>
          ) : (
            (v.plan.label ?? (
              <NotProvided>código {v.plan.code}, fora da lista de planos do ERP</NotProvided>
            ))
          )}
        </Fact>
        <Fact label="Benefícios" wide>
          {v.productsSyncedAt === null ? (
            <NotProvided>
              ainda não consultados no ERP — o “Atualizar do ERP” da ficha do veículo os
              traz na hora
            </NotProvided>
          ) : products.length === 0 ? (
            <NotProvided>o ERP não liga benefício a este veículo</NotProvided>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {products.map((p) => (
                <li key={p.erpCode} className="flex flex-wrap items-center gap-2">
                  <span>{p.label ?? `Produto ${p.erpCode}`}</span>
                  {p.erpStatus !== null && p.erpStatus !== 'ATIVO' && (
                    <Badge tone="neutro">{p.erpStatus.toLowerCase()}</Badge>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Fact>
      </dl>
      {v.productsSyncedAt !== null && (
        <p className="mt-3 text-xs text-texto-suave">
          Consultados no ERP em {formatInstant(v.productsSyncedAt)}.
        </p>
      )}
    </Section>
  );
}

function ThirdPartyData({ claim }: { claim: ClaimSummary }) {
  const query = useThirdParty(claim.thirdPartyId);
  const person = query.data;
  const vehicle = person?.vehicles.find((x) => x.id === claim.thirdPartyVehicleId);
  const phone = person?.phone ?? claim.thirdParty?.phone;

  return (
    <>
      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
      {query.isLoading && (
        <p className="text-sm text-texto-suave">Carregando o terceiro…</p>
      )}

      <Section title="Contato">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
          <Fact label="CPF ou CNPJ">
            {person?.document ? (
              <span className="tabular-nums">{formatDocument(person.document)}</span>
            ) : (
              <NotProvided />
            )}
          </Fact>
          <Fact label="Telefone">
            {phone ? (
              <span className="tabular-nums">{formatPhone(phone)}</span>
            ) : (
              <NotProvided />
            )}
          </Fact>
          <Fact label="E-mail" wide>
            {person?.email ?? <NotProvided />}
          </Fact>
        </dl>
      </Section>

      <Section title="No sinistro">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
          <Fact label="Ligado ao" wide>
            aviso #{claim.parentClaimId}
          </Fact>
          {claim.thirdPartyVehicleId === null ? (
            <Fact label="Veículo" wide>
              <NotProvided>sem veículo — muro, portão, poste…</NotProvided>
            </Fact>
          ) : (
            <>
              <Fact label="Placa">
                <span className="inline-flex items-center gap-2">
                  <span className="font-mono tabular-nums">
                    {vehicle?.plate ?? claim.thirdParty?.plate}
                  </span>
                  {vehicle?.vehicleId != null && (
                    <Badge tone="informacao">carro de associado</Badge>
                  )}
                </span>
              </Fact>
              {vehicle && (
                <Fact label="Ano">
                  <span className="tabular-nums">
                    {vehicle.manufactureYear}/{vehicle.modelYear}
                  </span>
                </Fact>
              )}
              <Fact label="Modelo" wide>
                {vehicle?.model ?? claim.thirdParty?.model ?? <NotProvided />}
              </Fact>
            </>
          )}
        </dl>
      </Section>
    </>
  );
}
